// Phase 10 — Admin Affiliate Command Center
import React, { useEffect, useState, useCallback } from "react";
import {
  BarChart3, Users, TrendingUp, DollarSign, RefreshCw,
  Search, MousePointerClick, Sparkles,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

function dollars(c) { if (c == null) return "—"; return `$${(c/100).toFixed(2)}`; }

export default function AdminAffiliateCommandCenter() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/affiliates/analytics");
      setData(d);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="max-w-7xl mx-auto px-4 py-10 text-rose-300" data-testid="acc-error">{err}</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-10 text-sm text-lifted-dim">Loading…</div>;

  const filteredAff = (data.top_affiliates || []).filter(
    (a) => !q || (a.slug || "").toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-affiliate-command">
      <header className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Admin · Affiliate</div>
          <h1 className="font-anton text-3xl uppercase">Command Center</h1>
        </div>
        <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="acc-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="acc-kpis">
        <Kpi label="Clicks" value={data.totals.clicks} icon={MousePointerClick} testid="acc-kpi-clicks" />
        <Kpi label="Leads" value={data.totals.leads} icon={Users} testid="acc-kpi-leads" />
        <Kpi label="Conversions" value={data.totals.conversions} icon={TrendingUp} testid="acc-kpi-conv" />
        <Kpi label="Revenue" value={dollars(data.totals.revenue_cents)} icon={DollarSign} testid="acc-kpi-revenue" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-panel overflow-hidden" data-testid="acc-top-affiliates">
          <div className="px-3 py-2 flex items-center gap-2 border-b border-white/5">
            <Sparkles className="h-3 w-3 text-lifted-green" />
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Top affiliates</div>
            <div className="ml-auto relative">
              <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-lifted-dim" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search slug"
                     className="bg-black/40 border border-white/10 rounded-md pl-7 pr-2 py-1 text-xs"
                     data-testid="acc-search" />
            </div>
          </div>
          {filteredAff.length === 0 ? (
            <div className="p-4 text-xs text-lifted-dim">No conversions yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                <tr className="border-b border-white/5">
                  <th className="px-3 py-2 text-left">Affiliate</th>
                  <th className="px-3 py-2 text-right">Conv.</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {filteredAff.map((a) => (
                  <tr key={a.affiliate_id} className="border-b border-white/5" data-testid={`acc-aff-${a.affiliate_id}`}>
                    <td className="px-3 py-2 font-mono">{a.slug || a.affiliate_id.slice(0,12)}</td>
                    <td className="px-3 py-2 text-right">{a.conversions}</td>
                    <td className="px-3 py-2 text-right">{dollars(a.revenue_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="glass-panel overflow-hidden" data-testid="acc-top-products">
          <div className="px-3 py-2 flex items-center gap-2 border-b border-white/5">
            <BarChart3 className="h-3 w-3 text-lifted-green" />
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Top products by views</div>
          </div>
          {(!data.top_products || data.top_products.length === 0) ? (
            <div className="p-4 text-xs text-lifted-dim">No tracked product views yet.</div>
          ) : (
            <ul className="divide-y divide-white/5 text-xs">
              {data.top_products.map((p) => (
                <li key={p.handle} className="px-3 py-2 flex items-center justify-between" data-testid={`acc-prod-${p.handle}`}>
                  <span className="truncate">{p.handle}</span>
                  <span className="text-lifted-dim">{p.views} views</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className="h-3.5 w-3.5 text-lifted-green" /> {label}
      </div>
      <div className="font-anton text-3xl text-white mt-1">{value}</div>
    </div>
  );
}
